import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Check, Loader2 } from "lucide-react";
import { Member } from "@/types";
import { getMemberDisplayName } from "../constants";
import { CountBadge } from "@/components/shared/CountBadge";

interface Country {
  id: number;
  amazon_country: string;
}

interface EditBrandDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editBrandName: string;
  onEditBrandNameChange: (name: string) => void;
  editBrandCountryId: string;
  onEditBrandCountryIdChange: (id: string) => void;
  editAssignedUserIds: number[];
  onToggleAssignedUser: (userId: number) => void;
  onSelectAllMembers: () => void;
  allMembersSelected: boolean;
  activeMembers: Member[];
  countries: Country[];
  products: number | undefined;
  onSave: () => void;
  onCancel: () => void;
  isUpdating: boolean;
  isAssigning: boolean;
}

export function EditBrandDialog({
  open,
  onOpenChange,
  editBrandName,
  onEditBrandNameChange,
  editBrandCountryId,
  onEditBrandCountryIdChange,
  editAssignedUserIds,
  onToggleAssignedUser,
  onSelectAllMembers,
  allMembersSelected,
  activeMembers,
  countries,
  products,
  onSave,
  onCancel,
  isUpdating,
  isAssigning,
}: EditBrandDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px] bg-card">
        <DialogHeader>
          <DialogTitle>Edit Brand</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Brand Name</Label>
            <Input value={editBrandName} onChange={(e) => onEditBrandNameChange(e.target.value)} className="h-9 rounded-lg bg-card" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Marketplace</Label>
            <Select value={editBrandCountryId} onValueChange={onEditBrandCountryIdChange}>
              <SelectTrigger className="h-9 rounded-lg bg-card">
                <SelectValue placeholder="Select marketplace" />
              </SelectTrigger>
              <SelectContent className="bg-popover border border-border">
                {countries.filter((country) => country.amazon_country === "US").map((country) => (
                  <SelectItem key={country.id} value={String(country.id)}>
                    Amazon {country.amazon_country}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs inline-flex items-center gap-1.5">
                Assigned Users <CountBadge size="sm" count={editAssignedUserIds.length} /> selected
              </Label>
              {activeMembers.length > 0 && (
                <button
                  type="button"
                  onClick={onSelectAllMembers}
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  {allMembersSelected ? "Deselect All" : "Select All"}
                </button>
              )}
            </div>
            <div className={`border rounded-lg max-h-[200px] overflow-y-auto divide-y divide-border ${editAssignedUserIds.length === 0 ? "border-destructive" : "border-border"}`}>
              {activeMembers.map((member) => {
                const isSelected = editAssignedUserIds.includes(member.id);
                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => onToggleAssignedUser(member.id)}
                    className="flex items-center gap-2.5 w-full px-3 py-2 hover:bg-muted/30 text-sm text-foreground transition-colors text-left"
                  >
                    <div className={`h-4 w-4 rounded-[4px] border flex items-center justify-center shrink-0 transition-colors ${isSelected ? "bg-primary border-primary" : "border-border"}`}>
                      {isSelected && <Check className="h-3 w-3 text-primary-foreground" />}
                    </div>
                    <span>{getMemberDisplayName(member)}</span>
                    <span className="text-xs text-muted-foreground ml-auto capitalize">{member.role}</span>
                  </button>
                );
              })}
              {activeMembers.length === 0 && (
                <div className="px-3 py-2 text-sm text-muted-foreground">No active members</div>
              )}
            </div>
            {editAssignedUserIds.length === 0 && (
              <p className="text-xs text-destructive">At least one user must be assigned.</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Products</Label>
            <p className="text-sm text-muted-foreground">{products} products tracked</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="rounded-lg">Cancel</Button>
          <Button onClick={onSave} className="rounded-lg" disabled={isUpdating || isAssigning || editAssignedUserIds.length === 0 || !editBrandName.trim()}>
            {(isUpdating || isAssigning) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
